import { useState, useEffect, useMemo, useCallback } from "react";
import { axiosInstance } from "../App";
import { downloadFileWithProgress } from "../lib/downloadProgress";
import { useNavigate } from "react-router-dom";
import { Baby, Plus, X, Users, Edit, Trash2, Camera, XCircle, UserPlus, Download, ClipboardList, MessageCircle, Send, Loader2, AlertTriangle, Copy, Merge, Type, Calendar, Mail, Eye, History, Share2, Frame } from "lucide-react";
import FramedPhoto from "../components/frames/FramedPhoto";
import ShareFrameDialog from "../components/frames/ShareFrameDialog";
import FrameSettingsDialog from "../components/frames/FrameSettingsDialog";
import { useActiveFrames, framesForChild } from "../components/frames/useActiveFrames";
import { FRAME_META } from "../components/frames/frameArt";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import ExportButton from "../components/common/ExportButton";
import { PageLayout } from "../components/layout/Sidebar";
import useFeaturePermissions from "../hooks/useFeaturePermissions";
import useModuleAccess from "../hooks/useModuleAccess";
import usePackages from "../hooks/usePackages";
import { useClasses } from "../hooks/useClasses";
import { LazyImage } from "../components/common/LazyImage";
import { FloatingElements, AnimatedHeader, AnimatedCard } from "../components/ui/AnimatedElements";
import BulkRegistrationImport from "../components/admin/BulkRegistrationImport";
import { ChildAttendanceWidget, AttendanceBadge } from "../components/widgets/ChildAttendanceWidget";
import { ChildStatusBadges } from "../components/UpdateStatusBadge";

export default function ChildrenPage({ user, onLogout }) {
  const navigate = useNavigate();
  const { hasAccess } = useFeaturePermissions(user?.role);
  const { isModuleOn } = useModuleAccess(user?.role);
  const subscriptionPackageOn = isModuleOn('subscription_package');
  const { packages: packagesList, getPackage } = usePackages();
  const [children, setChildren] = useState([]);
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showCreateParentDialog, setShowCreateParentDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [invitingChild, setInvitingChild] = useState(null);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [inviteData, setInviteData] = useState({ parent_name: "", parent_phone: "", parent_email: "" });
  const [invitePreview, setInvitePreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showInviteLogDialog, setShowInviteLogDialog] = useState(false);
  const [inviteLogs, setInviteLogs] = useState([]);
  const [loadingInviteLogs, setLoadingInviteLogs] = useState(false);
  const [resendingLogId, setResendingLogId] = useState(null);
  const [showBulkInviteDialog, setShowBulkInviteDialog] = useState(false);
  const [bulkEligible, setBulkEligible] = useState([]);
  const [loadingBulkEligible, setLoadingBulkEligible] = useState(false);
  const [sendingBulkInvite, setSendingBulkInvite] = useState(false);
  const [exportingLogs, setExportingLogs] = useState(false);
  const [showNotLoggedInDialog, setShowNotLoggedInDialog] = useState(false);
  const [notLoggedInParents, setNotLoggedInParents] = useState([]);
  const [loadingNotLoggedIn, setLoadingNotLoggedIn] = useState(false);
  const [editingChild, setEditingChild] = useState(null);
  const [selectedClass, setSelectedClass] = useState("all");
  const [selectedPackage, setSelectedPackage] = useState("all");
  const [selectedBranch, setSelectedBranch] = useState("all");
  const [selectedLetter, setSelectedLetter] = useState("all"); // Filter A-Z
  const [selectedRegistrationStatus, setSelectedRegistrationStatus] = useState("all"); // Filter pendaftaran
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const [confirmMergeAll, setConfirmMergeAll] = useState(false);
  const activeFrames = useActiveFrames();
  const [shareChild, setShareChild] = useState(null);
  const [showFrameSettings, setShowFrameSettings] = useState(false);
  const [mergingAll, setMergingAll] = useState(false);
  const [viewImageUrl, setViewImageUrl] = useState(null);
  const [viewImageName, setViewImageName] = useState("");
  const [expandedAttendance, setExpandedAttendance] = useState(null); // Track which child's attendance is expanded
  // Duplicate management states
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicates, setDuplicates] = useState({ total_duplicates: 0, groups: [] });
  const [loadingDuplicates, setLoadingDuplicates] = useState(false);
  const [deletingDuplicate, setDeletingDuplicate] = useState(null);
  const [fixingDob, setFixingDob] = useState(false);
  const [fixingIc, setFixingIc] = useState(false);
  const [uppercasing, setUppercasing] = useState(false);
  const [downloadingIncomplete, setDownloadingIncomplete] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    dob: "",
    gender: "",
    allergies: "",
    parent_ids: [],
    teacher_id: "",
    class_name: "",
    package: "",
    branch: ""
  });
  const [newParentData, setNewParentData] = useState({
    name: "",
    email: "",
    password: "",
    whatsapp: ""
  });
  const [availableClasses, setAvailableClasses] = useState([]);
  const [searchQuery, setSearchQuery] = useState(""); // Search by name
  const [statusDate, setStatusDate] = useState(new Date().toISOString().split('T')[0]); // Date for status check
  
  // Generate alphabet array for A-Z filter
  const alphabet = useMemo(() => ['all', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')], []);

  // Classes available (dinamik dari DB) - exclude Pre-school untuk guru (pendidik)
  const { classes: allClasses } = useClasses();
  const classes = user?.role === 'guru'
    ? allClasses.filter(c => !c.toLowerCase().startsWith('pre-school'))
    : allClasses;

  const fetchChildren = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/children');
      setChildren(response.data);
      // Extract unique class names from actual children data
      const uniqueClasses = [...new Set(response.data.map(c => c.class_name).filter(Boolean))];
      setAvailableClasses(uniqueClasses.sort());
    } catch (error) {
      toast.error("Ralat mendapatkan data kanak-kanak");
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users");
    }
  }, []);

  const fetchBranches = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/branches');
      setBranches(response.data);
    } catch (error) {
      console.error("Error fetching branches");
    }
  }, []);

  const fetchRegistrations = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/child-registration');
      setRegistrations(response.data);
    } catch (error) {
      console.error("Error fetching registrations");
    }
  }, []);

  // Check if child has registration
  // Check if registration is COMPLETE (all 4 required sections filled)
  const isRegistrationComplete = useCallback((reg) => {
    if (!reg) return false;
    
    // Check 1: Maklumat Persetujuan (Consent Information)
    const hasConsent = !!(
      reg.consent_parent_name && 
      reg.consent_parent_ic && 
      reg.consent_child_name &&
      reg.consent_signature
    );
    
    // Check 2: Maklumat Kanak-kanak (Child Information)
    const hasChildInfo = !!(
      reg.child_full_name &&
      reg.child_ic_number &&
      reg.child_dob &&
      reg.child_gender
    );
    
    // Check 3: Maklumat Ibu/Penjaga (Mother/Guardian Information)
    const motherInfo = reg.mother_info || {};
    const hasMotherInfo = !!(
      motherInfo.name &&
      motherInfo.ic_number &&
      motherInfo.phone_hp
    );
    
    // Check 4: Maklumat Bapa/Penjaga (Father/Guardian Information)
    const fatherInfo = reg.father_info || {};
    const hasFatherInfo = !!(
      fatherInfo.name &&
      fatherInfo.ic_number &&
      fatherInfo.phone_hp
    );
    
    return hasConsent && hasChildInfo && hasMotherInfo && hasFatherInfo;
  }, []);

  // Helper to normalize DOB for comparison (remove time portion)
  const normalizeDob = (dob) => {
    if (!dob) return '';
    // Handle both "2024-10-28" and "2024-10-28 00:00:00" formats
    return dob.split(' ')[0].split('T')[0];
  };

  // Check if child has COMPLETE registration (either registration_id exists or found by name/IC matching)
  // Only count as "complete" if status is "approved" (not "incomplete")
  const hasRegistration = useCallback((child) => {
    // Method 1: Check if registration has child_id that matches this child
    const regByChildId = registrations.find(r => r.child_id === child.id && r.status === 'approved');
    if (regByChildId) {
      return true;
    }
    
    // Method 2: Check if child has registration_id linked
    if (child.registration_id) {
      const linkedReg = registrations.find(r => r.id === child.registration_id);
      // Only count as complete if status is "approved" (not "incomplete" or other)
      if (linkedReg && linkedReg.status === 'approved') {
        return true;
      }
    }
    
    // Method 3: Try to find registration by name matching
    const childNameLower = child.name?.toLowerCase().trim();
    const childDobNorm = normalizeDob(child.dob);
    
    const reg = registrations.find(r => {
      // Skip incomplete registrations
      if (r.status !== 'approved') return false;
      
      const regNameLower = r.child_full_name?.toLowerCase().trim();
      const consentNameLower = r.consent_child_name?.toLowerCase().trim();
      const regDobNorm = normalizeDob(r.child_dob);
      
      // Match by name (case insensitive)
      const nameMatch = (regNameLower && childNameLower === regNameLower) ||
                       (consentNameLower && childNameLower === consentNameLower);
      
      if (nameMatch) {
        // If DOB available, also check DOB
        if (childDobNorm && regDobNorm) {
          return childDobNorm === regDobNorm;
        }
        // If no DOB, just match by name
        return true;
      }
      return false;
    });
    
    // Return true if registration is approved
    return reg && reg.status === 'approved';
  }, [registrations]);

  // Get registration for a child
  const getChildRegistration = useCallback((childName, childDob) => {
    const childDobNorm = normalizeDob(childDob);
    return registrations.find(reg => {
      const regNameLower = reg.child_full_name?.toLowerCase().trim();
      const consentNameLower = reg.consent_child_name?.toLowerCase().trim();
      const childNameLower = childName?.toLowerCase().trim();
      const regDobNorm = normalizeDob(reg.child_dob);
      
      const nameMatch = (regNameLower && childNameLower === regNameLower) ||
                       (consentNameLower && childNameLower === consentNameLower);
      
      if (nameMatch && childDobNorm && regDobNorm) {
        return childDobNorm === regDobNorm;
      }
      return nameMatch;
    });
  }, [registrations]);

  // Navigate to registration page with child data pre-filled
  const handleCreateRegistration = (child) => {
    // Navigate to registration page with child data as query params
    const params = new URLSearchParams({
      child_name: child.name,
      child_dob: child.dob,
      child_id: child.id,
      from_children: 'true'
    });
    navigate(`/child-registration?${params.toString()}`);
  };

  useEffect(() => {
    fetchChildren();
    if (user.role === 'admin' || user.role === 'guru' || user.role === 'superadmin') {
      fetchUsers();
      fetchBranches();
      fetchRegistrations();
    }
  }, [user.role, fetchChildren, fetchUsers, fetchBranches, fetchRegistrations]);

  // Helper to check registration status for filtering
  const checkChildRegistration = useCallback((child) => {
    // Find linked registration first
    let reg = null;
    
    // Method 1: Check if registration has child_id that matches this child
    reg = registrations.find(r => r.child_id === child.id && r.status === 'approved');
    
    // Method 2: Check if child has registration_id linked
    if (!reg && child.registration_id) {
      const linkedReg = registrations.find(r => r.id === child.registration_id);
      if (linkedReg && linkedReg.status === 'approved') {
        reg = linkedReg;
      }
    }
    
    // Method 3: Try to find registration by name matching
    if (!reg) {
      const childNameLower = child.name?.toLowerCase().trim();
      const childDobNorm = normalizeDob(child.dob);
      
      reg = registrations.find(r => {
        const regNameLower = r.child_full_name?.toLowerCase().trim();
        const consentNameLower = r.consent_child_name?.toLowerCase().trim();
        const regDobNorm = normalizeDob(r.child_dob);
        
        // Match by name (case insensitive)
        const nameMatch = (regNameLower && childNameLower === regNameLower) ||
                         (consentNameLower && childNameLower === consentNameLower);
        
        if (nameMatch) {
          // If DOB available, also check DOB
          if (childDobNorm && regDobNorm) {
            return childDobNorm === regDobNorm && r.status === 'approved';
          }
          // If no DOB, just match by name and approved status
          return r.status === 'approved';
        }
        return false;
      });
    }
    
    // Check if registration is approved AND data is complete
    if (reg && reg.status === 'approved') {
      // Also check if data is complete (has mandatory fields)
      const hasChildIC = reg.child_ic_number && reg.child_ic_number !== 'None' && reg.child_ic_number.trim() !== '';
      const hasChildName = reg.child_full_name && reg.child_full_name.trim() !== '';
      const hasChildDob = reg.child_dob && reg.child_dob.trim() !== '';
      
      const motherIC = reg.mother_info?.ic_number || '';
      const fatherIC = reg.father_info?.ic_number || '';
      const hasParentIC = (motherIC && motherIC !== 'None' && motherIC.trim() !== '') || 
                          (fatherIC && fatherIC !== 'None' && fatherIC.trim() !== '');
      
      // Return true only if all mandatory fields are present
      return hasChildName && hasChildIC && hasChildDob && hasParentIC;
    }
    
    return false;
  }, [registrations]);

  // Use useMemo instead of useEffect + setState for filtering
  const filteredChildren = useMemo(() => {
    let filtered = children;
    
    // Filter by class
    if (selectedClass !== "all") {
      filtered = filtered.filter(c => c.class_name === selectedClass);
    }
    
    // Filter by package
    if (selectedPackage !== "all") {
      if (selectedPackage === "unset") {
        filtered = filtered.filter(c => !c.package);
      } else {
        filtered = filtered.filter(c => c.package === selectedPackage);
      }
    }
    
    // Filter by branch (for superadmin)
    if (selectedBranch !== "all" && user.role === 'superadmin') {
      if (selectedBranch === "unset") {
        filtered = filtered.filter(c => !c.branch);
      } else {
        filtered = filtered.filter(c => c.branch === selectedBranch);
      }
    }
    
    // Filter by letter A-Z
    if (selectedLetter !== "all") {
      filtered = filtered.filter(c => {
        const firstLetter = (c.name || "").charAt(0).toUpperCase();
        return firstLetter === selectedLetter;
      });
    }
    
    // Filter by registration status
    if (selectedRegistrationStatus !== "all") {
      if (selectedRegistrationStatus === "registered") {
        filtered = filtered.filter(c => checkChildRegistration(c));
      } else if (selectedRegistrationStatus === "not_registered") {
        filtered = filtered.filter(c => !checkChildRegistration(c));
      }
    }
    
    // Sort alphabetically by name
    filtered.sort((a, b) => (a.name || "").localeCompare(b.name || "", 'ms'));
    
    // Filter by search query (name)
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      filtered = filtered.filter(c => 
        (c.name || "").toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [selectedClass, selectedPackage, selectedBranch, selectedLetter, selectedRegistrationStatus, children, user.role, checkChildRegistration, searchQuery]);

  // Download Excel template with incomplete/unregistered children data
  const downloadIncompleteExcelTemplate = async () => {
    setDownloadingIncomplete(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      await downloadFileWithProgress({
        label: 'Template Pendaftaran Tidak Lengkap (Excel)',
        filename: `Pendaftaran_Tidak_Lengkap_${today}.xlsx`,
        request: (onDownloadProgress) => axiosInstance.get('/child-registration/export/incomplete-template', {
          responseType: 'blob',
          onDownloadProgress
        })
      });

      toast.success('Template Excel berjaya dimuat turun!');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error(error.response?.data?.detail || 'Gagal memuat turun template Excel');
    } finally {
      setDownloadingIncomplete(false);
    }
  };

  // Fetch duplicates
  const fetchDuplicates = useCallback(async () => {
    if (user.role !== 'admin' && user.role !== 'superadmin') return;
    
    setLoadingDuplicates(true);
    try {
      const response = await axiosInstance.get('/children/duplicates');
      setDuplicates(response.data);
    } catch (error) {
      console.error("Error fetching duplicates:", error);
    } finally {
      setLoadingDuplicates(false);
    }
  }, [user.role]);

  // Delete a duplicate child
  const handleDeleteDuplicate = async (childId, childName) => {
    if (!window.confirm(`Adakah anda pasti mahu memadam "${childName}"? Tindakan ini tidak boleh dibatalkan.`)) {
      return;
    }

    setDeletingDuplicate(childId);
    try {
      await axiosInstance.delete(`/children/duplicates/${childId}`);
      toast.success(`"${childName}" berjaya dipadam!`);
      fetchDuplicates();
      fetchChildren();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ralat memadam kanak-kanak");
    } finally {
      setDeletingDuplicate(null);
    }
  };

  // Merge duplicates - keep original, delete others
  const handleMergeDuplicates = async (originalId, duplicateIds, name) => {
    if (!window.confirm(`Gabungkan ${duplicateIds.length} rekod duplicate untuk "${name}"? Semua data aktiviti akan digabungkan ke rekod asal.`)) {
      return;
    }

    try {
      await axiosInstance.post('/children/duplicates/merge', {
        original_id: originalId,
        duplicate_ids: duplicateIds
      });
      toast.success(`Berjaya gabungkan ${duplicateIds.length} rekod duplicate!`);
      fetchDuplicates();
      fetchChildren();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ralat menggabungkan rekod");
    }
  };

  // Fix DOB format - normalize all DOB to YYYY-MM-DD (remove time portion)
  const handleFixDobFormat = async () => {
    if (!window.confirm("Ini akan normalisasikan format tarikh lahir semua rekod (contoh: '2024-10-28 00:00:00' → '2024-10-28'). Teruskan?")) {
      return;
    }

    setFixingDob(true);
    try {
      const response = await axiosInstance.post('/child-registration/fix-dob-format');
      
      const { children_fixed, registrations_fixed, links_fixed, duplicates_detected, duplicate_details } = response.data;
      
      toast.success(
        <div>
          <p className="font-bold">Data Diperbaiki!</p>
          <p className="text-sm">Format DOB: {children_fixed + registrations_fixed} rekod</p>
          {links_fixed > 0 && (
            <p className="text-sm text-green-600">✅ {links_fixed} link pendaftaran diperbaiki</p>
          )}
          {duplicates_detected > 0 && (
            <p className="text-sm text-orange-600 font-bold mt-1">⚠️ {duplicates_detected} duplicate dikesan</p>
          )}
        </div>,
        { duration: 8000 }
      );
      
      if (duplicates_detected > 0) {
        // Auto open duplicates dialog
        setDuplicates({
          total_duplicates: duplicates_detected,
          groups: duplicate_details || []
        });
        setShowDuplicateDialog(true);
      }
      
      fetchChildren();
      fetchRegistrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ralat memperbaiki format DOB");
    } finally {
      setFixingDob(false);
    }
  };

  // Convert all existing data to UPPERCASE
  const handleUppercaseAll = async () => {
    if (!window.confirm("Ini akan menukar SEMUA data sedia ada kepada HURUF BESAR. Teruskan?")) {
      return;
    }

    setUppercasing(true);
    try {
      const response = await axiosInstance.post('/child-registration/uppercase-all-data');
      
      const { children_updated, registrations_updated, total_children, total_registrations } = response.data;
      
      toast.success(
        <div>
          <p className="font-bold">✅ HURUF BESAR Selesai!</p>
          <p className="text-sm">Kanak-kanak: {children_updated}/{total_children} dikemaskini</p>
          <p className="text-sm">Pendaftaran: {registrations_updated}/{total_registrations} dikemaskini</p>
        </div>,
        { duration: 8000 }
      );
      
      fetchChildren();
      fetchRegistrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ralat menukar kepada huruf besar");
    } finally {
      setUppercasing(false);
    }
  };

  // Fix IC format - normalize all IC numbers to XXXXXX-XX-XXXX format
  const handleFixIcFormat = async () => {
    if (!window.confirm("Ini akan membetulkan format semua No. Kad Pengenalan kepada format XXXXXX-XX-XXXX. Contoh: '930915085884.0' → '930915-08-5884'. Teruskan?")) {
      return;
    }

    setFixingIc(true);
    try {
      const response = await axiosInstance.post('/child-registration/fix-ic-format');
      
      const { children_fixed, registrations_fixed, registrations_revalidated, fixed_details } = response.data;
      
      toast.success(
        <div>
          <p className="font-bold">✅ Format IC Diperbaiki!</p>
          <p className="text-sm">Kanak-kanak: {children_fixed} rekod diperbaiki</p>
          <p className="text-sm">Pendaftaran: {registrations_fixed} rekod diperbaiki</p>
          {registrations_revalidated > 0 && (
            <p className="text-sm text-orange-600 font-bold">⚠️ {registrations_revalidated} pendaftaran dikembalikan ke &quot;tidak lengkap&quot;</p>
          )}
        </div>,
        { duration: 8000 }
      );
      
      fetchChildren();
      fetchRegistrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ralat memperbaiki format IC");
    } finally {
      setFixingIc(false);
    }
  };

  // Auto-merge duplicates (superadmin only)
  // Gabung SEMUA kumpulan duplicate sekali gus (sama seperti "Gabung Semua" setiap kumpulan)
  const handleMergeAllDuplicates = async () => {
    setMergingAll(true);
    try {
      const response = await axiosInstance.post('/children/duplicates/merge-all');
      const { groups_merged, records_merged } = response.data;
      toast.success(
        <div>
          <p className="font-bold">Gabung Semua Selesai!</p>
          <p className="text-sm">{groups_merged} kumpulan digabung • {records_merged} rekod duplicate dipindah ke rekod ASAL</p>
        </div>,
        { duration: 8000 }
      );
      setConfirmMergeAll(false);
      fetchChildren();
      fetchDuplicates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ralat menggabungkan duplicate");
    } finally {
      setMergingAll(false);
    }
  };


  const handleCreateParent = async (e) => {
    e.preventDefault();
    if (!newParentData.email?.trim()) {
      toast.error("Email wajib diisi untuk ibu bapa terima maklumat login");
      return;
    }
    try {
      const response = await axiosInstance.post('/admin/create-user', {
        name: newParentData.name,
        email: newParentData.email.trim(),
        password: newParentData.password,
        whatsapp: newParentData.whatsapp,
        role: 'ibu_bapa'
      });
      toast.success("Ibu bapa berjaya didaftarkan!");
      if (response.data.email_sent) {
        toast.success(`📧 Maklumat login dihantar ke ${newParentData.email.trim()}`);
      } else {
        toast.warning("Email tidak dapat dihantar. Sila semak alamat email.");
      }
      // Add new parent to form
      setFormData({...formData, parent_ids: [...formData.parent_ids, response.data.id]});
      setShowCreateParentDialog(false);
      setNewParentData({ name: "", email: "", password: "", whatsapp: "" });
      fetchUsers(); // Refresh users list
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ralat mendaftarkan ibu bapa");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.gender) {
      toast.error("Sila pilih jantina kanak-kanak");
      return;
    }
    
    try {
      if (editingChild) {
        await axiosInstance.put(`/children/${editingChild.id}`, formData);
        toast.success("Kanak-kanak berjaya dikemaskini!");
      } else {
        await axiosInstance.post('/children', formData);
        toast.success("Kanak-kanak berjaya ditambah!");
      }
      setShowDialog(false);
      setEditingChild(null);
      setFormData({ name: "", dob: "", gender: "", allergies: "", parent_ids: [], teacher_id: "", class_name: "", package: "", branch: "" });
      fetchChildren();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ralat menyimpan data");
    }
  };

  const handleEdit = (child) => {
    setEditingChild(child);
    setFormData({
      name: child.name,
      dob: child.dob,
      gender: child.gender || "",
      allergies: child.allergies || "",
      parent_ids: child.parent_ids || [],
      teacher_id: child.teacher_id || "",
      class_name: child.class_name || "",
      package: child.package || "",
      branch: child.branch || ""
    });
    setShowDialog(true);
  };

  const handleDelete = async (childId) => {
    if (!window.confirm("Adakah anda pasti mahu memadam kanak-kanak ini? Semua data berkaitan akan dipadam.")) return;
    
    try {
      await axiosInstance.delete(`/children/${childId}`);
      toast.success("Kanak-kanak berjaya dipadam!");
      fetchChildren();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ralat memadam data");
    }
  };

  const handlePhotoUpload = async (childId, file) => {
    if (!file) return;
    
    // Check file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error("Saiz gambar terlalu besar. Maksimum 5MB.");
      return;
    }
    
    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error("Sila pilih fail gambar yang sah (JPG, PNG, dll)");
      return;
    }
    
    setUploadingPhoto(childId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axiosInstance.post(`/children/${childId}/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success("Gambar berjaya dimuat naik!");
      fetchChildren();
    } catch (error) {
      console.error("Photo upload error:", error);
      const errorMsg = error.response?.data?.detail || error.message || "Ralat memuat naik gambar";
      toast.error(errorMsg);
    } finally {
      setUploadingPhoto(null);
    }
  };

  const handlePhotoRemove = async (childId) => {
    if (!window.confirm("Adakah anda pasti mahu memadam gambar ini?")) return;
    
    try {
      await axiosInstance.delete(`/children/${childId}/photo`);
      toast.success("Gambar berjaya dipadam!");
      fetchChildren();
    } catch (error) {
      toast.error("Ralat memadam gambar");
    }
  };

  // Handler untuk hantar WhatsApp invitation ke parent
  const handleOpenInviteDialog = (child) => {
    setInvitingChild(child);
    setInviteData({ parent_name: "", parent_phone: "", parent_email: "" });
    setShowInviteDialog(true);
  };

  const handlePreviewInvitation = async (e) => {
    e.preventDefault();
    
    if (!inviteData.parent_name.trim() || !inviteData.parent_phone.trim()) {
      toast.error("Sila isi nama dan no telefon ibu bapa");
      return;
    }

    if (!inviteData.parent_email.trim()) {
      toast.error("Email wajib diisi untuk ibu bapa terima maklumat login");
      return;
    }

    setLoadingPreview(true);
    try {
      const response = await axiosInstance.post(`/children/${invitingChild.id}/invite-parent/preview`, {
        child_id: invitingChild.id,
        parent_name: inviteData.parent_name.trim(),
        parent_phone: inviteData.parent_phone.trim(),
        parent_email: inviteData.parent_email.trim()
      });
      setInvitePreview(response.data);
      setShowPreviewDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ralat menjana pratonton emel");
    } finally {
      setLoadingPreview(false);
    }
  };

  const fetchInviteLogs = async () => {
    setLoadingInviteLogs(true);
    try {
      const res = await axiosInstance.get('/admin/email-invitation-logs?limit=100');
      setInviteLogs(res.data.logs || []);
    } catch (error) {
      toast.error("Ralat memuatkan log jemputan email");
    } finally {
      setLoadingInviteLogs(false);
    }
  };

  const handleResendLog = async (logId) => {
    setResendingLogId(logId);
    try {
      const res = await axiosInstance.post(`/admin/email-invitation-logs/${logId}/resend`);
      if (res.data.email_sent) {
        toast.success("📧 Emel berjaya dihantar semula", { duration: 6000 });
      } else {
        toast.error(res.data.message || "Penghantaran gagal — semak log untuk butiran", { duration: 6000 });
      }
      fetchInviteLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ralat menghantar semula emel");
    } finally {
      setResendingLogId(null);
    }
  };

  const handleExportLogs = async () => {
    setExportingLogs(true);
    try {
      await downloadFileWithProgress({
        label: 'Log Jemputan Email (Excel)',
        filename: `log_jemputan_email_${new Date().toISOString().slice(0, 10)}.xlsx`,
        request: (onDownloadProgress) => axiosInstance.get('/admin/email-invitation-logs/export', { responseType: 'blob', onDownloadProgress })
      });
      toast.success("📥 Log berjaya dieksport ke Excel");
    } catch (error) {
      toast.error("Ralat mengeksport log");
    } finally {
      setExportingLogs(false);
    }
  };

  const fetchNotLoggedInParents = async () => {
    setLoadingNotLoggedIn(true);
    try {
      const res = await axiosInstance.get('/admin/parents-not-logged-in');
      setNotLoggedInParents(res.data.parents || []);
    } catch (error) {
      toast.error("Ralat memuatkan senarai ibu bapa belum log masuk");
    } finally {
      setLoadingNotLoggedIn(false);
    }
  };

  const openBulkInviteDialog = async () => {
    setShowBulkInviteDialog(true);
    setLoadingBulkEligible(true);
    try {
      const res = await axiosInstance.get('/admin/bulk-invite-parents/eligible');
      setBulkEligible(res.data.children || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ralat memuatkan senarai jemputan pukal");
    } finally {
      setLoadingBulkEligible(false);
    }
  };

  const handleBulkInvite = async () => {
    setSendingBulkInvite(true);
    try {
      const res = await axiosInstance.post('/admin/bulk-invite-parents');
      toast.success(res.data.message, { duration: 8000 });
      setShowBulkInviteDialog(false);
      fetchChildren();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ralat menghantar jemputan pukal");
    } finally {
      setSendingBulkInvite(false);
    }
  };

  const handleSendInvitation = async () => {
    setSendingInvite(true);
    try {
      const response = await axiosInstance.post(`/children/${invitingChild.id}/invite-parent`, {
        child_id: invitingChild.id,
        parent_name: inviteData.parent_name.trim(),
        parent_phone: inviteData.parent_phone.trim(),
        parent_email: inviteData.parent_email.trim()
      });

      if (response.data.success) {
        if (response.data.parent_exists) {
          toast.success("Notifikasi dihantar! Anak ditambah ke akaun sedia ada.", {
            duration: 5000
          });
        } else {
          toast.success(
            <div>
              <p className="font-bold">Jemputan dihantar!</p>
              <p className="text-sm">Akaun baru dibuat untuk {inviteData.parent_name}</p>
              {response.data.temp_password && (
                <p className="text-xs mt-1 bg-yellow-100 p-1 rounded">
                  Password sementara: <strong>{response.data.temp_password}</strong>
                </p>
              )}
            </div>,
            { duration: 10000 }
          );
        }
        
        if (response.data.email_sent) {
          toast.success(`📧 Email jemputan berjaya dihantar ke ${inviteData.parent_email.trim()}`, {
            duration: 5000
          });
        } else {
          toast.warning("Email tidak dapat dihantar. Sila semak alamat email atau konfigurasi Resend.", {
            duration: 5000
          });
        }
        
        setShowPreviewDialog(false);
        setInvitePreview(null);
        setShowInviteDialog(false);
        fetchChildren();
        fetchUsers();
      }
    } catch (error) {
      console.error("Invitation error:", error);
      toast.error(error.response?.data?.detail || "Ralat menghantar jemputan");
    } finally {
      setSendingInvite(false);
    }
  };

  const parents = users.filter(u => u.role === 'ibu_bapa');
  const teachers = users.filter(u => u.role === 'guru');

  // Prepare export data
  const exportData = filteredChildren.map(child => {
    const parentNames = child.parent_ids?.map(pid => {
      const parent = parents.find(p => p.id === pid);
      return parent?.name || '';
    }).filter(Boolean).join(', ');
    
    const teacher = teachers.find(t => t.id === child.teacher_id);
    
    return {
      'Nama': child.name,
      'Tarikh Lahir': child.dob ? new Date(child.dob).toLocaleDateString('ms-MY') : '-',
      'Kelas': child.class_name || '-',
      'Ibu Bapa': parentNames || '-',
      'Guru': teacher?.name || '-',
      'Alahan': typeof child.allergies === 'object' ? 
        Object.entries(child.allergies)
          .filter(([k, v]) => v && !k.includes('_details'))
          .map(([k]) => k)
          .join(', ') || '-'
        : (child.allergies || '-')
    };
  });

  return (
    <PageLayout user={user} onLogout={onLogout} hasAccess={hasAccess}>
      <FloatingElements variant="default" />
      <div className="max-w-6xl mx-auto relative z-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <AnimatedHeader 
            emoji="👶"
            title="Kanak-kanak"
            subtitle="🎈 Senarai kanak-kanak berdaftar"
            delay={100}
          />
          <div className="flex items-center gap-2 flex-wrap">
            {(user.role === 'admin' || user.role === 'superadmin') && (
              <>
                {/* UPPERCASE All Data Button */}
                <Button
                  data-testid="uppercase-all-btn"
                  onClick={handleUppercaseAll}
                  disabled={uppercasing}
                  variant="outline"
                  className="border-purple-300 text-purple-600 hover:bg-purple-50 rounded-2xl font-bold text-sm"
                >
                  {uppercasing ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Type className="w-4 h-4 mr-1" />
                  )}
                  ABC
                </Button>
                {/* Fix IC Format Button */}
                <Button
                  data-testid="fix-ic-btn"
                  onClick={handleFixIcFormat}
                  disabled={fixingIc}
                  variant="outline"
                  className="border-green-300 text-green-600 hover:bg-green-50 rounded-2xl font-bold text-sm"
                >
                  {fixingIc ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <ClipboardList className="w-4 h-4 mr-1" />
                  )}
                  Fix IC
                </Button>
                {/* Fix DOB Format Button */}
                <Button
                  data-testid="fix-dob-btn"
                  onClick={handleFixDobFormat}
                  disabled={fixingDob}
                  variant="outline"
                  className="border-blue-300 text-blue-600 hover:bg-blue-50 rounded-2xl font-bold text-sm"
                >
                  {fixingDob ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 mr-1" />
                  )}
                  Fix DOB
                </Button>
                {user.role === "superadmin" && (
                  <Button
                    data-testid="frame-settings-btn"
                    onClick={() => setShowFrameSettings(true)}
                    variant="outline"
                    className="border-pink-300 text-pink-600 hover:bg-pink-50 rounded-2xl font-bold text-sm"
                  >
                    <Frame className="w-4 h-4 mr-1" />
                    Bingkai Bermusim
                  </Button>
                )}
                {/* Duplicate Detection Button */}
                <Button
                  data-testid="check-duplicates-btn"
                  onClick={() => {
                    fetchDuplicates();
                    setShowDuplicateDialog(true);
                  }}
                  variant="outline"
                  className="border-orange-300 text-orange-600 hover:bg-orange-50 rounded-2xl font-bold text-sm"
                >
                  <Copy className="w-4 h-4 mr-1" />
                  Semak Duplicate
                </Button>
                <Button
                  data-testid="invite-log-btn"
                  onClick={() => {
                    setShowInviteLogDialog(true);
                    fetchInviteLogs();
                  }}
                  variant="outline"
                  className="border-pink-300 text-pink-600 hover:bg-pink-50 rounded-2xl font-bold text-sm"
                >
                  <Mail className="w-4 h-4 mr-1" />
                  Log Jemputan
                </Button>
                <Button
                  data-testid="not-logged-in-btn"
                  onClick={() => {
                    setShowNotLoggedInDialog(true);
                    fetchNotLoggedInParents();
                  }}
                  variant="outline"
                  className="border-amber-300 text-amber-600 hover:bg-amber-50 rounded-2xl font-bold text-sm"
                >
                  <AlertTriangle className="w-4 h-4 mr-1" />
                  Belum Login
                </Button>
                <Button
                  data-testid="bulk-invite-btn"
                  onClick={openBulkInviteDialog}
                  variant="outline"
                  className="border-emerald-300 text-emerald-600 hover:bg-emerald-50 rounded-2xl font-bold text-sm"
                >
                  <UserPlus className="w-4 h-4 mr-1" />
                  Jemputan Pukal
                </Button>
                <BulkRegistrationImport onImportComplete={() => {
                  fetchChildren();
                  fetchRegistrations();
                }} />
                <ExportButton
                  dataType="children"
                  buttonText="Export"
                />
                <Button
                  data-testid="add-child-button"
                  onClick={() => setShowDialog(true)}
                  className="bg-gradient-to-r from-pink-400 to-pink-500 hover:from-pink-500 hover:to-pink-600 text-white rounded-2xl font-bold text-sm"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Tambah
                </Button>
              </>
            )}
          </div>
        </div>

          {/* Search Box */}
          <AnimatedCard className="mb-4" delay={120} color="green">
            <h3 className="text-sm font-black text-gray-700 mb-3 flex items-center gap-2">
              <span className="text-lg">🔍</span> Cari Kanak-kanak:
            </h3>
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Taip nama kanak-kanak..."
                className="w-full px-4 py-3 pl-12 rounded-2xl border-2 border-green-200 focus:border-green-400 focus:ring-2 focus:ring-green-200 outline-none transition-all text-gray-700 font-medium"
                data-testid="search-children-input"
              />
              <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-xl">🔍</span>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              )}
            </div>
            {searchQuery && (
              <p className="text-xs text-green-600 mt-2 font-medium">
                📊 {filteredChildren.length} hasil carian untuk "{searchQuery}"
              </p>
            )}
          </AnimatedCard>

          {/* A-Z Filter - Admin/Guru/Superadmin */}
          {(user.role === 'admin' || user.role === 'guru' || user.role === 'superadmin') && (
            <AnimatedCard className="mb-4" delay={150} color="blue">
              <h3 className="text-sm font-black text-gray-700 mb-3 flex items-center gap-2">
                <span className="text-lg">🔤</span> Pilih A-Z:
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {alphabet.map(letter => {
                  const count = letter === 'all' 
                    ? children.length 
                    : children.filter(c => (c.name || "").charAt(0).toUpperCase() === letter).length;
                  
                  return (
                    <button
                      key={letter}
                      onClick={() => setSelectedLetter(letter)}
                      disabled={letter !== 'all' && count === 0}
                      className={`w-9 h-9 rounded-xl font-bold text-sm transition-all duration-200 ${
                        selectedLetter === letter
                          ? 'bg-gradient-to-r from-blue-400 to-blue-500 text-white shadow-md scale-110'
                          : count === 0 && letter !== 'all'
                          ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                          : 'bg-gray-100 text-gray-600 hover:bg-blue-100 hover:scale-105'
                      }`}
                    >
                      {letter === 'all' ? '✨' : letter}
                    </button>
                  );
                })}
              </div>
              {selectedLetter !== 'all' && (
                <p className="text-xs text-blue-600 mt-2 font-medium">
                  📊 {filteredChildren.length} kanak-kanak bermula dengan &quot;{selectedLetter}&quot;
                </p>
              )}
            </AnimatedCard>
          )}

          {/* Class Filter - Admin/Guru/Superadmin */}
          {(user.role === 'admin' || user.role === 'guru' || user.role === 'superadmin') && (
            <AnimatedCard className="mb-6" delay={200} color="purple">
              <h3 className="text-sm font-black text-gray-700 mb-3 flex items-center gap-2">
                <span className="text-lg">🎓</span> Pilih mengikut Kelas:
              </h3>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedClass("all")}
                  className={`px-4 py-2.5 rounded-2xl font-bold text-sm transition-all duration-300 ${
                    selectedClass === "all"
                      ? 'bg-gradient-to-r from-pink-400 to-pink-500 text-white shadow-lg scale-105'
                      : 'bg-gray-100 text-gray-600 hover:bg-pink-100 hover:scale-105'
                  }`}
                >
                  🎨 Semua ({children.length})
                </button>
                {classes.map(className => {
                  const count = children.filter(c => c.class_name === className).length;
                  const emoji = className.includes('Baby') ? '🍼' : 
                               className.includes('Toddler I') ? '🧸' : 
                               className.includes('Toddler II') ? '🎨' : 
                               className.includes('Nursery') ? '🌈' : '📚';
                  return (
                    <button
                      key={className}
                      onClick={() => setSelectedClass(className)}
                      className={`px-4 py-2.5 rounded-2xl font-bold text-sm transition-all duration-300 ${
                        selectedClass === className
                          ? 'bg-gradient-to-r from-purple-400 to-purple-500 text-white shadow-lg scale-105'
                          : count === 0
                          ? 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                          : 'bg-gray-100 text-gray-600 hover:bg-purple-100 hover:scale-105'
                      }`}
                    >
                      {emoji} {className.split(' (')[0]} ({count})
                    </button>
                  );
                })}
              </div>
              
              {/* Package Filter (kawalan module: subscription_package) */}
              {subscriptionPackageOn && (
              <>
              <h3 className="text-sm font-black text-gray-700 mb-3 mt-5 flex items-center gap-2">
                <span className="text-lg">📦</span> Pilih mengikut Pakej:
              </h3>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedPackage("all")}
                  className={`px-4 py-2.5 rounded-2xl font-bold text-sm transition-all duration-300 ${
                    selectedPackage === "all"
                      ? 'bg-gradient-to-r from-pink-400 to-pink-500 text-white shadow-lg scale-105'
                      : 'bg-gray-100 text-gray-600 hover:bg-pink-100 hover:scale-105'
                  }`}
                >
                  🎯 Semua
                </button>
                {packagesList.map((pkg) => (
                  <button
                    key={pkg.code}
                    onClick={() => setSelectedPackage(pkg.code)}
                    className={`px-4 py-2.5 rounded-2xl font-bold text-sm transition-all duration-300 ${
                      selectedPackage === pkg.code
                        ? 'text-white shadow-lg scale-105'
                        : 'bg-gray-100 text-gray-600 hover:bg-purple-100 hover:scale-105'
                    }`}
                    style={selectedPackage === pkg.code ? { backgroundColor: pkg.color || '#9178D6' } : {}}
                  >
                    {pkg.emoji} {pkg.name} ({children.filter(c => c.package === pkg.code).length})
                  </button>
                ))}
                <button
                  onClick={() => setSelectedPackage("unset")}
                  className={`px-4 py-2.5 rounded-2xl font-bold text-sm transition-all duration-300 ${
                    selectedPackage === "unset"
                      ? 'bg-gradient-to-r from-orange-400 to-orange-500 text-white shadow-lg scale-105'
                      : 'bg-gray-100 text-gray-600 hover:bg-orange-100 hover:scale-105'
                  }`}
                >
                  ⚠️ Belum Set ({children.filter(c => !c.package).length})
                </button>
              </div>
              </>
              )}

              {/* Branch Filter - Superadmin Only */}
              {user.role === 'superadmin' && branches.length > 0 && (
                <>
                  <h3 className="text-sm font-black text-gray-700 mb-3 mt-5 flex items-center gap-2">
                    <span className="text-lg">🏢</span> Pilih mengikut Cawangan:
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setSelectedBranch("all")}
                      className={`px-4 py-2.5 rounded-2xl font-bold text-sm transition-all duration-300 ${
                        selectedBranch === "all"
                          ? 'bg-gradient-to-r from-pink-400 to-pink-500 text-white shadow-lg scale-105'
                          : 'bg-gray-100 text-gray-600 hover:bg-pink-100 hover:scale-105'
                      }`}
                    >
                      🎯 Semua
                    </button>
                    {branches.map(branch => (
                      <button
                        key={branch.id}
                        onClick={() => setSelectedBranch(branch.name)}
                        className={`px-4 py-2.5 rounded-2xl font-bold text-sm transition-all duration-300 ${
                          selectedBranch === branch.name
                            ? 'bg-gradient-to-r from-cyan-400 to-cyan-500 text-white shadow-lg scale-105'
                            : 'bg-gray-100 text-gray-600 hover:bg-cyan-100 hover:scale-105'
                        }`}
                      >
                        📍 {branch.name} ({children.filter(c => c.branch === branch.name).length})
                      </button>
                    ))}
                    <button
                      onClick={() => setSelectedBranch("unset")}
                      className={`px-4 py-2.5 rounded-2xl font-bold text-sm transition-all duration-300 ${
                        selectedBranch === "unset"
                          ? 'bg-gradient-to-r from-red-400 to-red-500 text-white shadow-lg scale-105'
                          : 'bg-gray-100 text-gray-600 hover:bg-red-100 hover:scale-105'
                      }`}
                    >
                      ⚠️ Tiada Cawangan ({children.filter(c => !c.branch).length})
                    </button>
                  </div>
                </>
              )}

              {/* Registration Status Filter - Admin/Superadmin */}
              <h3 className="text-sm font-black text-gray-700 mb-3 mt-5 flex items-center gap-2">
                <span className="text-lg">📋</span> Pilih mengikut Status Pendaftaran:
              </h3>
              <div className="flex flex-wrap gap-2">
                <button
                  data-testid="filter-registration-all"
                  onClick={() => setSelectedRegistrationStatus("all")}
                  className={`px-4 py-2.5 rounded-2xl font-bold text-sm transition-all duration-300 ${
                    selectedRegistrationStatus === "all"
                      ? 'bg-gradient-to-r from-pink-400 to-pink-500 text-white shadow-lg scale-105'
                      : 'bg-gray-100 text-gray-600 hover:bg-pink-100 hover:scale-105'
                  }`}
                >
                  🎯 Semua
                </button>
                <button
                  data-testid="filter-registration-complete"
                  onClick={() => setSelectedRegistrationStatus("registered")}
                  className={`px-4 py-2.5 rounded-2xl font-bold text-sm transition-all duration-300 ${
                    selectedRegistrationStatus === "registered"
                      ? 'bg-gradient-to-r from-green-400 to-green-500 text-white shadow-lg scale-105'
                      : 'bg-gray-100 text-gray-600 hover:bg-green-100 hover:scale-105'
                  }`}
                >
                  ✅ Pendaftaran Lengkap ({children.filter(c => checkChildRegistration(c)).length})
                </button>
                <button
                  data-testid="filter-registration-incomplete"
                  onClick={() => setSelectedRegistrationStatus("not_registered")}
                  className={`px-4 py-2.5 rounded-2xl font-bold text-sm transition-all duration-300 ${
                    selectedRegistrationStatus === "not_registered"
                      ? 'bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-lg scale-105'
                      : 'bg-gray-100 text-gray-600 hover:bg-amber-100 hover:scale-105'
                  }`}
                >
                  ⚠️ Belum Buat Pendaftaran ({children.filter(c => !checkChildRegistration(c)).length})
                </button>
                
                {/* Download button - only show when "Belum Buat Pendaftaran" is selected */}
                {selectedRegistrationStatus === "not_registered" && (user.role === 'admin' || user.role === 'superadmin') && (
                  <button
                    data-testid="download-incomplete-template"
                    onClick={downloadIncompleteExcelTemplate}
                    disabled={downloadingIncomplete}
                    className="px-4 py-2.5 rounded-2xl font-bold text-sm transition-all duration-300 bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:from-emerald-600 hover:to-teal-700 shadow-lg hover:scale-105 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {downloadingIncomplete ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Memuat turun...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4" />
                        Muat Turun Excel PTL/BBP
                      </>
                    )}
                  </button>
                )}
              </div>
            </AnimatedCard>
          )}
          
          {/* Class Filter for Parents - only show classes with their children */}
          {user.role === 'ibu_bapa' && availableClasses.length > 0 && (
            <AnimatedCard className="mb-6" delay={200} color="pink">
              <h3 className="text-sm font-black text-gray-700 mb-3 flex items-center gap-2">
                <span className="text-lg">🎓</span> Pilih mengikut Kelas:
              </h3>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedClass("all")}
                  className={`px-4 py-2.5 rounded-2xl font-bold text-sm transition-all duration-300 ${
                    selectedClass === "all"
                      ? 'bg-gradient-to-r from-pink-400 to-pink-500 text-white shadow-lg scale-105'
                      : 'bg-gray-100 text-gray-600 hover:bg-pink-100 hover:scale-105'
                  }`}
                >
                  🎨 Semua ({children.length})
                </button>
                {availableClasses.map(className => (
                  <button
                    key={className}
                    onClick={() => setSelectedClass(className)}
                    className={`px-4 py-2.5 rounded-2xl font-bold text-sm transition-all duration-300 ${
                      selectedClass === className
                        ? 'bg-gradient-to-r from-purple-400 to-purple-500 text-white shadow-lg scale-105'
                        : 'bg-gray-100 text-gray-600 hover:bg-purple-100 hover:scale-105'
                    }`}
                  >
                    {className} ({children.filter(c => c.class_name === className).length})
                  </button>
                ))}
              </div>
            </AnimatedCard>
          )}

          {/* Date Selector for Status Check */}
          {(user.role === "admin" || user.role === "guru" || user.role === "superadmin" || user.role === "branch_manager") && (
            <AnimatedCard className="mb-6" delay={250} color="blue">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3" data-testid="date-selector-status">
                <div className="flex items-center gap-3 w-full sm:w-auto">
                  <div className="w-10 h-10 min-w-[2.5rem] rounded-xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center shadow-lg">
                    <Calendar className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 sm:flex-none">
                    <label className="text-sm font-black text-gray-700 block whitespace-nowrap">
                      Pilih Tarikh untuk Semak Status:
                    </label>
                    <input
                      type="date"
                      value={statusDate}
                      onChange={(e) => setStatusDate(e.target.value)}
                      data-testid="status-date-picker"
                      className="mt-1 px-4 py-2.5 border-2 border-blue-300 rounded-2xl font-bold text-sm w-full sm:w-auto focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition bg-white"
                    />
                  </div>
                </div>
                <div className="text-xs text-gray-500 bg-blue-50 rounded-xl p-2.5 w-full sm:w-auto sm:flex-1">
                  <p>Status <span className="font-bold text-blue-600">Aktiviti</span>, <span className="font-bold text-green-600">Menu</span>, dan <span className="font-bold text-purple-600">Log Harian</span> akan dipaparkan berdasarkan tarikh ini.</p>
                </div>
              </div>
            </AnimatedCard>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredChildren.map((child, idx) => (
              <AnimatedCard
                key={child.id}
                data-testid={`child-card-${child.id}`}
                className="hover:scale-[1.02]"
                delay={300 + (idx * 50)}
                color="pink"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="relative group">
                    {activeFrames?.show_on_cards && framesForChild(child, activeFrames)[0] ? (
                      <FramedPhoto
                        src={child.photo_url}
                        name={child.name}
                        frame={framesForChild(child, activeFrames)[0]}
                        size={64}
                        className="m-2 cursor-pointer"
                        imgClassName="shadow-lg"
                        onClick={() => { if (child.photo_url) { setViewImageUrl(child.photo_url); setViewImageName(child.name); } }}
                        fallback={
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center shadow-lg">
                            <span className="text-2xl font-black text-white">{child.name?.charAt(0)?.toUpperCase() || 'A'}</span>
                          </div>
                        }
                      />
                    ) : child.photo_url ? (
                      <div className="relative">
                        <LazyImage
                          src={child.photo_url}
                          alt={child.name}
                          className="w-16 h-16 rounded-2xl cursor-pointer hover:ring-4 hover:ring-pink-300 transition-all object-cover shadow-lg"
                          onClick={() => {
                            setViewImageUrl(child.photo_url);
                            setViewImageName(child.name);
                          }}
                          fallbackColor="bg-gradient-to-br from-pink-300 to-purple-300"
                          fallbackIcon={<Baby className="w-8 h-8 text-white" />}
                        />
                        {(["superadmin", "branch_manager", "admin", "guru"].includes(user.role) || 
                          (user.role === "ibu_bapa" && child.parent_ids?.includes(user.id))) && (
                          <button
                            onClick={() => handlePhotoRemove(child.id)}
                            className="absolute -top-1 -right-1 p-1 bg-red-500 hover:bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                            title="Padam gambar"
                          >
                            <XCircle className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center shadow-lg">
                        <span className="text-2xl font-black text-white">
                          {child.name?.charAt(0)?.toUpperCase() || '👶'}
                        </span>
                      </div>
                    )}
                    
                    {(["superadmin", "branch_manager", "admin", "guru"].includes(user.role) || 
                      (user.role === "ibu_bapa" && child.parent_ids?.includes(user.id))) && (
                      <label className="absolute -bottom-1 -right-1 p-1.5 bg-gradient-to-r from-blue-400 to-blue-500 hover:from-blue-500 hover:to-blue-600 text-white rounded-full cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
                        <Camera className="w-3 h-3" />
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              handlePhotoUpload(child.id, e.target.files[0]);
                            }
                          }}
                          disabled={uploadingPhoto === child.id}
                        />
                      </label>
                    )}
                  </div>
                  
                  {(user.role === "admin" || user.role === "guru" || user.role === "superadmin") && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(child)}
                        className="p-2 bg-gradient-to-r from-blue-400 to-blue-500 hover:from-blue-500 hover:to-blue-600 text-white rounded-xl transition-all shadow-md hover:shadow-lg hover:scale-105"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      {(user.role === "admin" || user.role === "superadmin") && (
                        <button
                          onClick={() => handleDelete(child.id)}
                          className="p-2 bg-gradient-to-r from-red-400 to-red-500 hover:from-red-500 hover:to-red-600 text-white rounded-xl transition-all shadow-md hover:shadow-lg hover:scale-105"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <h3 className="font-black text-xl text-gray-800 mb-3">{child.name}</h3>
                {framesForChild(child, activeFrames).length > 0 && (
                  <button
                    onClick={() => setShareChild(child)}
                    className="mb-3 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-bold text-white bg-pink-500 hover:bg-pink-600 shadow-sm transition-colors"
                    data-testid={`share-frame-btn-${child.id}`}
                  >
                    <Share2 className="w-3.5 h-3.5" /> Kongsi Gambar {FRAME_META[framesForChild(child, activeFrames)[0]]?.label}
                  </button>
                )}
                
                {/* Status Badges - NEW FEATURE */}
                {(user.role === "admin" || user.role === "guru" || user.role === "superadmin" || user.role === "branch_manager") && (
                  <div className="mb-3">
                    <ChildStatusBadges
                      childId={child.id}
                      date={statusDate}
                      className="flex-wrap"
                    />
                  </div>
                )}
                
                {uploadingPhoto === child.id && (
                  <p className="text-xs text-blue-600 font-bold mb-2">Memuat naik gambar...</p>
                )}
                <div className="space-y-3">
                  <p className="text-gray-600 font-medium text-sm">
                    <span className="text-gray-800 font-bold">🎂 Tarikh Lahir:</span> {child.dob}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {child.class_name && (
                      <span className="px-3 py-1.5 bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-700 rounded-2xl text-xs font-bold shadow-sm">
                        🎓 {child.class_name.split(' (')[0]}
                      </span>
                    )}
                    {subscriptionPackageOn && child.package && (
                      <span
                        className="px-3 py-1.5 rounded-2xl text-xs font-bold shadow-sm text-white"
                        style={{ backgroundColor: getPackage(child.package)?.color || '#9178D6' }}
                      >
                        {getPackage(child.package)?.emoji} {getPackage(child.package)?.name}
                      </span>
                    )}
                    {subscriptionPackageOn && !child.package && (user.role === 'admin' || user.role === 'superadmin') && (
                      <span className="px-3 py-1.5 bg-gradient-to-r from-orange-100 to-amber-100 text-orange-600 rounded-2xl text-xs font-bold shadow-sm">
                        ⚠️ Pakej belum set
                      </span>
                    )}
                    {/* Branch Badge */}
                    {child.branch && (user.role === 'superadmin' || user.role === 'admin') && (
                      <span className="px-3 py-1.5 bg-gradient-to-r from-cyan-100 to-teal-100 text-cyan-700 rounded-2xl text-xs font-bold shadow-sm">
                        📍 {child.branch.replace('Destiny Junior ', '')}
                      </span>
                    )}
                    {!child.branch && (user.role === 'superadmin' || user.role === 'admin') && (
                      <span className="px-3 py-1.5 bg-gradient-to-r from-red-100 to-rose-100 text-red-600 rounded-2xl text-xs font-bold shadow-sm">
                        ⚠️ Tiada cawangan
                      </span>
                    )}
                  </div>
                  {child.allergies && (
                    <div className="bg-red-50 border-2 border-red-200 rounded-xl px-3 py-2">
                      <p className="text-red-600 text-sm font-bold">
                        ⚠️ Alahan: {typeof child.allergies === 'object' ? 
                          Object.entries(child.allergies)
                            .filter(([k, v]) => v && !k.includes('_details'))
                            .map(([k, v]) => {
                              const details = child.allergies[`${k}_details`];
                              return `${k.charAt(0).toUpperCase() + k.slice(1)}${details ? `: ${details}` : ''}`;
                            })
                            .join(', ') || 'Ada alahan'
                          : child.allergies}
                      </p>
                    </div>
                  )}
                  
                  {/* Parent Names - Show mother/father for admin, superadmin, branch_manager, guru */}
                  {(user.role === "admin" || user.role === "superadmin" || user.role === "branch_manager" || user.role === "guru") && child.parent_ids?.length > 0 && (
                    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-xl px-3 py-2">
                      <p className="text-indigo-700 text-xs font-bold">
                        👨‍👩‍👧 Ibu Bapa: {child.parent_names?.length > 0 
                          ? child.parent_names.join(', ')
                          : child.parent_ids.map(pid => {
                              const parent = parents.find(p => p.id === pid);
                              return parent?.name || '';
                            }).filter(Boolean).join(', ') || 'Tiada maklumat'}
                      </p>
                    </div>
                  )}
                  
                  {/* Tarikh Dicipta - hanya untuk admin, superadmin, pengurus/branch_manager */}
                  {(user.role === "admin" || user.role === "superadmin" || user.role === "pengurus" || user.role === "branch_manager") && child.created_at && (
                    <p className="text-gray-500 text-xs mt-2" data-testid={`child-created-at-${child.id}`}>
                      📅 Dicipta: {new Date(child.created_at).toLocaleString('ms-MY', {
                        day: '2-digit',
                        month: '2-digit', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  )}
                  
                  {/* Kehadiran Section */}
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <button
                      onClick={() => setExpandedAttendance(expandedAttendance === child.id ? null : child.id)}
                      className="w-full py-2 px-3 bg-gradient-to-r from-purple-100 to-pink-100 hover:from-purple-200 hover:to-pink-200 text-purple-700 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2"
                      data-testid={`attendance-btn-${child.id}`}
                    >
                      <Calendar className="w-4 h-4" />
                      {expandedAttendance === child.id ? "Sembunyikan Kehadiran" : "Lihat Kehadiran"}
                      <AttendanceBadge childId={child.id} />
                    </button>
                    
                    {/* Expanded Attendance Widget */}
                    {expandedAttendance === child.id && (
                      <ChildAttendanceWidget childId={child.id} childName={child.name} />
                    )}
                  </div>
                  
                  {/* Registration Status & Button */}
                  {(user.role === "admin" || user.role === "superadmin") && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      {checkChildRegistration(child) ? (
                        <div className="flex items-center justify-between">
                          <span className="px-3 py-1.5 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 rounded-2xl text-xs font-bold shadow-sm flex items-center gap-1">
                            ✅ Pendaftaran Lengkap
                          </span>
                          <button
                            onClick={() => {
                              const reg = getChildRegistration(child.name, child.dob);
                              if (reg) navigate(`/child-registration/${reg.id}`);
                            }}
                            className="text-xs text-blue-600 hover:text-blue-800 font-bold underline"
                          >
                            Lihat
                          </button>
                        </div>
                      ) : getChildRegistration(child.name, child.dob) ? (
                        // Has registration but NOT complete
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="px-3 py-1.5 bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700 rounded-2xl text-xs font-bold shadow-sm flex items-center gap-1">
                              ⚠️ Pendaftaran Tidak Lengkap
                            </span>
                            <button
                              onClick={() => {
                                const reg = getChildRegistration(child.name, child.dob);
                                if (reg) navigate(`/child-registration/${reg.id}`);
                              }}
                              className="text-xs text-blue-600 hover:text-blue-800 font-bold underline"
                            >
                              Lengkapkan
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleCreateRegistration(child)}
                          className="w-full py-2 px-3 bg-gradient-to-r from-amber-400 to-orange-400 hover:from-amber-500 hover:to-orange-500 text-white rounded-xl font-bold text-sm transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                        >
                          <ClipboardList className="w-4 h-4" />
                          Buat Pendaftaran
                        </button>
                      )}
                      
                      {/* Butang Hantar Invitation WhatsApp */}
                      <button
                        data-testid={`invite-parent-btn-${child.id}`}
                        onClick={() => handleOpenInviteDialog(child)}
                        className="w-full mt-2 py-2 px-3 bg-gradient-to-r from-green-400 to-emerald-500 hover:from-green-500 hover:to-emerald-600 text-white rounded-xl font-bold text-sm transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                      >
                        <MessageCircle className="w-4 h-4" />
                        Hantar Jemputan
                      </button>
                    </div>
                  )}
                </div>
              </AnimatedCard>
            ))}
          </div>

          {filteredChildren.length === 0 && (
            <AnimatedCard className="text-center" delay={300} color="pink">
              <div data-testid="no-children-message" className="py-12">
                <span className="text-6xl block mb-4">👶</span>
                <p className="text-gray-600 font-bold">
                  {children.length === 0 ? "Tiada kanak-kanak didaftarkan" : "Tiada kanak-kanak dalam kelas ini"}
                </p>
              </div>
            </AnimatedCard>
          )}
          
          {/* Bottom spacing for bottom navigation */}
          <div className="h-24 sm:h-8"></div>
        </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md max-h-[75vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingChild ? "Edit Kanak-kanak" : "Tambah Kanak-kanak Baru"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 pb-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Nama</label>
              <input
                data-testid="child-name-input"
                type="text"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-pink-400 focus:ring-2 focus:ring-pink-200 transition-all outline-none"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                placeholder="Nama kanak-kanak"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Tarikh Lahir</label>
              <input
                data-testid="child-dob-input"
                type="date"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-pink-400 focus:ring-2 focus:ring-pink-200 transition-all outline-none"
                value={formData.dob}
                onChange={(e) => setFormData({...formData, dob: e.target.value})}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Jantina</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({...formData, gender: 'Lelaki'})}
                  className={`p-3 rounded-xl border-2 transition-all text-center font-medium ${
                    formData.gender === 'Lelaki' 
                      ? 'border-blue-500 bg-blue-50 text-blue-700' 
                      : 'border-gray-200 hover:border-gray-300 text-gray-600'
                  }`}
                >
                  👦 Lelaki
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({...formData, gender: 'Perempuan'})}
                  className={`p-3 rounded-xl border-2 transition-all text-center font-medium ${
                    formData.gender === 'Perempuan' 
                      ? 'border-pink-500 bg-pink-50 text-pink-700' 
                      : 'border-gray-200 hover:border-gray-300 text-gray-600'
                  }`}
                >
                  👧 Perempuan
                </button>
              </div>
              {!formData.gender && (
                <p className="text-xs text-red-500 mt-1">Sila pilih jantina kanak-kanak</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Alahan (jika ada)</label>
              <input
                data-testid="child-allergies-input"
                type="text"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-pink-400 focus:ring-2 focus:ring-pink-200 transition-all outline-none"
                value={formData.allergies}
                onChange={(e) => setFormData({...formData, allergies: e.target.value})}
                placeholder="Contoh: Kacang, Susu"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Kelas</label>
              <select
                data-testid="child-class-select"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-pink-400 focus:ring-2 focus:ring-pink-200 transition-all outline-none"
                value={formData.class_name}
                onChange={(e) => setFormData({...formData, class_name: e.target.value})}
              >
                <option value="">Auto (berdasarkan umur)</option>
                {classes.map(className => (
                  <option key={className} value={className}>{className}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Biarkan &quot;Auto&quot; untuk assign kelas berdasarkan umur kanak-kanak
              </p>
            </div>

            {/* Package Selection - Admin and Superadmin (kawalan module: subscription_package) */}
            {subscriptionPackageOn && (user.role === 'admin' || user.role === 'superadmin') && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Pakej</label>
                <div className="grid grid-cols-2 gap-3">
                  {packagesList.map((pkg) => (
                    <button
                      key={pkg.code}
                      type="button"
                      onClick={() => setFormData({...formData, package: pkg.code})}
                      className={`p-4 rounded-xl border-2 transition-all text-center ${
                        formData.package === pkg.code 
                          ? 'bg-purple-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={formData.package === pkg.code ? { borderColor: pkg.color || '#9178D6' } : {}}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-xl">{pkg.emoji}</span>
                        <span className="font-semibold text-gray-800">{pkg.name}</span>
                      </div>
                    </button>
                  ))}
                </div>
                {!formData.package && (
                  <p className="text-xs text-orange-500 mt-1">⚠️ Sila pilih pakej untuk kanak-kanak ini</p>
                )}
              </div>
            )}

            {/* Branch Selection - Admin/Superadmin Only */}
            {(user.role === 'admin' || user.role === 'superadmin') && branches.length > 0 && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Cawangan</label>
                <select
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-pink-400 focus:ring-2 focus:ring-pink-200 transition-all outline-none"
                  value={formData.branch}
                  onChange={(e) => setFormData({...formData, branch: e.target.value})}
                >
                  <option value="">-- Pilih Cawangan --</option>
                  {branches.map(branch => (
                    <option key={branch.id} value={branch.name}>
                      {branch.name} {branch.branch_code ? `(${branch.branch_code})` : ''}
                    </option>
                  ))}
                </select>
                {!formData.branch && (
                  <p className="text-xs text-orange-500 mt-1">⚠️ Sila pilih cawangan untuk kanak-kanak ini</p>
                )}
              </div>
            )}

            {user.role === 'admin' && (
              <>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-semibold text-gray-700">Ibu Bapa</label>
                    <button
                      type="button"
                      onClick={() => setShowCreateParentDialog(true)}
                      className="text-xs bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded-lg flex items-center gap-1 transition-colors"
                    >
                      <UserPlus className="w-3 h-3" />
                      Daftar Baru
                    </button>
                  </div>
                  <select
                    data-testid="child-parent-select"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-pink-400 focus:ring-2 focus:ring-pink-200 transition-all outline-none"
                    onChange={(e) => {
                      if (e.target.value && !formData.parent_ids.includes(e.target.value)) {
                        setFormData({...formData, parent_ids: [...formData.parent_ids, e.target.value]});
                      }
                    }}
                  >
                    <option value="">Pilih ibu bapa</option>
                    {parents.map(parent => (
                      <option key={parent.id} value={parent.id}>{parent.name} ({parent.email})</option>
                    ))}
                  </select>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.parent_ids.map(parentId => {
                      const parent = parents.find(p => p.id === parentId);
                      return parent ? (
                        <span key={parentId} className="bg-pink-100 text-pink-700 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                          {parent.name}
                          <button
                            type="button"
                            onClick={() => setFormData({...formData, parent_ids: formData.parent_ids.filter(id => id !== parentId)})}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Guru</label>
                  <select
                    data-testid="child-teacher-select"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-pink-400 focus:ring-2 focus:ring-pink-200 transition-all outline-none"
                    value={formData.teacher_id}
                    onChange={(e) => setFormData({...formData, teacher_id: e.target.value})}
                  >
                    <option value="">Auto (berdasarkan kelas)</option>
                    {teachers.map(teacher => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.name} {teacher.assigned_classes?.length > 0 ? `(${teacher.assigned_classes.join(', ')})` : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Biarkan &quot;Auto&quot; untuk assign guru berdasarkan kelas kanak-kanak
                  </p>
                </div>
              </>
            )}

            <Button
              data-testid="submit-child-button"
              type="submit"
              className="w-full bg-gradient-to-r from-pink-400 to-pink-500 hover:from-pink-500 hover:to-pink-600 text-white"
            >
              {editingChild ? "Kemaskini" : "Tambah Kanak-kanak"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Image Viewer Modal */}
      <Dialog open={!!viewImageUrl} onOpenChange={(open) => {
        if (!open) {
          setViewImageUrl(null);
          setViewImageName("");
        }
      }}>
        <DialogContent className="max-w-2xl p-2">
          <DialogHeader>
            <DialogTitle className="text-center">{viewImageName}</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-4">
            {viewImageUrl && (
              <img
                src={viewImageUrl}
                alt={viewImageName}
                className="max-w-full max-h-[70vh] object-contain rounded-xl shadow-lg"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Parent Dialog */}
      <Dialog open={showCreateParentDialog} onOpenChange={setShowCreateParentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-green-500" />
              Daftar Ibu Bapa Baru
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateParent} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Nama Penuh</label>
              <input
                type="text"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-400 focus:ring-2 focus:ring-green-200 transition-all outline-none"
                value={newParentData.name}
                onChange={(e) => setNewParentData({...newParentData, name: e.target.value})}
                required
                placeholder="Nama ibu bapa"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Nombor Telefon <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-400 focus:ring-2 focus:ring-green-200 transition-all outline-none"
                value={newParentData.whatsapp}
                onChange={(e) => setNewParentData({...newParentData, whatsapp: e.target.value})}
                required
                placeholder="Contoh: 0123456789"
              />
              <p className="text-xs text-green-600 mt-1">📱 Nombor ini akan digunakan untuk login</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Kata Laluan</label>
              <input
                type="password"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-400 focus:ring-2 focus:ring-green-200 transition-all outline-none"
                value={newParentData.password}
                onChange={(e) => setNewParentData({...newParentData, password: e.target.value})}
                required
                placeholder="Kata laluan"
                minLength={6}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-400 focus:ring-2 focus:ring-green-200 transition-all outline-none"
                value={newParentData.email}
                onChange={(e) => setNewParentData({...newParentData, email: e.target.value})}
                required
                placeholder="email@contoh.com"
              />
              <p className="text-xs text-gray-500 mt-1">📧 Maklumat login akan dihantar ke email ini</p>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-xl p-3">
              <p className="text-sm text-green-700">
                <span className="font-semibold">📱 Login:</span> Ibu bapa akan log masuk menggunakan nombor telefon dan kata laluan. Maklumat login akan dihantar melalui email.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateParentDialog(false)}
                className="flex-1"
              >
                Batal
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-green-400 to-green-500 hover:from-green-500 hover:to-green-600 text-white"
              >
                Daftar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Parent Invitation Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-green-500" />
              Hantar Jemputan
            </DialogTitle>
          </DialogHeader>
          
          {invitingChild && (
            <form onSubmit={handlePreviewInvitation} className="space-y-4">
              {/* Child Info */}
              <div className="bg-pink-50 border border-pink-200 rounded-xl p-3">
                <p className="text-sm text-pink-700 font-medium">
                  <span className="font-bold">👶 Anak:</span> {invitingChild.name}
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nama Ibu/Bapa <span className="text-red-500">*</span>
                </label>
                <input
                  data-testid="invite-parent-name"
                  type="text"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-400 focus:ring-2 focus:ring-green-200 transition-all outline-none"
                  value={inviteData.parent_name}
                  onChange={(e) => setInviteData({...inviteData, parent_name: e.target.value})}
                  required
                  placeholder="Contoh: Siti Aminah"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  data-testid="invite-parent-email"
                  type="email"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-400 focus:ring-2 focus:ring-green-200 transition-all outline-none"
                  value={inviteData.parent_email}
                  onChange={(e) => setInviteData({...inviteData, parent_email: e.target.value})}
                  required
                  placeholder="Contoh: siti@gmail.com"
                />
                <p className="text-xs text-gray-500 mt-1">
                  📧 Jemputan dan maklumat login akan dihantar ke email ini
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nombor Telefon (untuk login) <span className="text-red-500">*</span>
                </label>
                <input
                  data-testid="invite-parent-phone"
                  type="tel"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-400 focus:ring-2 focus:ring-green-200 transition-all outline-none"
                  value={inviteData.parent_phone}
                  onChange={(e) => setInviteData({...inviteData, parent_phone: e.target.value})}
                  required
                  placeholder="Contoh: 0123456789"
                />
                <p className="text-xs text-gray-500 mt-1">
                  📱 Nombor ini akan digunakan sebagai username login ibu bapa
                </p>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-xl p-3 space-y-2">
                <p className="text-sm text-green-700 font-semibold">📤 Apa yang akan dihantar melalui email:</p>
                <ul className="text-xs text-green-600 space-y-1 ml-4 list-disc">
                  <li>Link untuk install aplikasi</li>
                  <li>Arahan langkah demi langkah</li>
                  <li>Maklumat login (No. Telefon & Password)</li>
                  <li>Nama anak & cawangan</li>
                </ul>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <p className="text-xs text-amber-700">
                  <span className="font-semibold">💡 Nota:</span> Jika ibu bapa sudah ada akaun, anak akan ditambah ke akaun sedia ada dan notifikasi akan dihantar.
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowInviteDialog(false)}
                  className="flex-1"
                  disabled={loadingPreview}
                >
                  Batal
                </Button>
                <Button
                  data-testid="preview-invite-btn"
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-blue-400 to-indigo-500 hover:from-blue-500 hover:to-indigo-600 text-white"
                  disabled={loadingPreview}
                >
                  {loadingPreview ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Menjana...
                    </>
                  ) : (
                    <>
                      <Eye className="w-4 h-4 mr-2" />
                      Pratonton Emel
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Preview Dialog (Pratonton sebelum hantar) */}
      <Dialog open={showPreviewDialog} onOpenChange={(open) => { if (!sendingInvite) setShowPreviewDialog(open); }}>
        <DialogContent data-testid="email-preview-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-pink-500" />
              Pratonton Emel Jemputan
            </DialogTitle>
          </DialogHeader>
          {invitePreview && (
            <div className="space-y-3">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm space-y-1">
                <p data-testid="preview-recipient"><span className="font-bold">Kepada:</span> {invitePreview.recipient}</p>
                <p data-testid="preview-subject"><span className="font-bold">Subjek:</span> {invitePreview.subject}</p>
                {invitePreview.note && <p className="text-xs text-blue-600">💡 {invitePreview.note}</p>}
              </div>
              <div className="border-2 border-gray-200 rounded-xl overflow-hidden bg-white">
                <iframe
                  data-testid="email-preview-frame"
                  title="Pratonton Emel"
                  srcDoc={invitePreview.html}
                  sandbox=""
                  className="w-full"
                  style={{ height: "420px", border: "none" }}
                />
              </div>
              <div className="flex gap-3">
                <Button
                  data-testid="back-to-edit-btn"
                  type="button"
                  variant="outline"
                  className="flex-1"
                  disabled={sendingInvite}
                  onClick={() => setShowPreviewDialog(false)}
                >
                  Kembali
                </Button>
                <Button
                  data-testid="confirm-send-invite-btn"
                  type="button"
                  className="flex-1 bg-gradient-to-r from-green-400 to-emerald-500 hover:from-green-500 hover:to-emerald-600 text-white"
                  disabled={sendingInvite}
                  onClick={handleSendInvitation}
                >
                  {sendingInvite ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Menghantar...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Sahkan & Hantar
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Invitation Log Dialog */}
      <Dialog open={showInviteLogDialog} onOpenChange={setShowInviteLogDialog}>
        <DialogContent data-testid="invite-log-dialog" className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-purple-500" />
              Log Jemputan Email
            </DialogTitle>
          </DialogHeader>
          <div className="flex justify-end">
            <Button
              data-testid="export-invite-logs-btn"
              size="sm"
              variant="outline"
              className="border-green-300 text-green-600 hover:bg-green-50 rounded-xl text-xs font-bold"
              disabled={exportingLogs}
              onClick={handleExportLogs}
            >
              {exportingLogs ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <Download className="w-3 h-3 mr-1" />
              )}
              Export Excel
            </Button>
          </div>
          {loadingInviteLogs ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
              <span className="ml-3 text-gray-600 font-medium">Memuatkan log...</span>
            </div>
          ) : inviteLogs.length === 0 ? (
            <div data-testid="invite-log-empty" className="text-center py-10">
              <span className="text-5xl block mb-3">📭</span>
              <p className="text-gray-600 font-bold">Tiada log jemputan email lagi</p>
            </div>
          ) : (
            <div className="space-y-2">
              {inviteLogs.map((log) => (
                <div key={log.id} data-testid={`invite-log-item-${log.id}`} className="border border-gray-200 rounded-xl p-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-bold text-sm text-gray-800 truncate">📧 {log.recipient_email}</p>
                    <p className="text-xs text-gray-600">
                      {log.parent_name}
                      {log.child_name ? ` • Anak: ${log.child_name}` : ""}
                      {log.branch_name ? ` • ${log.branch_name}` : ""}
                    </p>
                    <p className="text-xs text-gray-500">
                      {log.created_at ? new Date(log.created_at).toLocaleString("ms-MY") : ""}
                      {log.sent_by_name ? ` • Dihantar oleh: ${log.sent_by_name}` : ""}
                      {log.invitation_type === "akaun_baru" ? " • Akaun Baru" : " • Akaun Sedia Ada"}
                    </p>
                    {log.error && <p className="text-xs text-red-500 mt-1">{log.error}</p>}
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold shrink-0 ${log.status === "sent" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
                    {log.status === "sent" ? "✅ Dihantar" : "❌ Gagal"}
                  </span>
                  {log.status === "failed" && (
                    <Button
                      data-testid={`resend-log-btn-${log.id}`}
                      size="sm"
                      variant="outline"
                      className="shrink-0 border-blue-300 text-blue-600 hover:bg-blue-50 rounded-xl text-xs font-bold"
                      disabled={resendingLogId === log.id}
                      onClick={() => handleResendLog(log.id)}
                    >
                      {resendingLogId === log.id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <>
                          <Send className="w-3 h-3 mr-1" />
                          Hantar Semula
                        </>
                      )}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Invite Dialog */}
      <Dialog open={showBulkInviteDialog} onOpenChange={(open) => { if (!sendingBulkInvite) setShowBulkInviteDialog(open); }}>
        <DialogContent data-testid="bulk-invite-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-emerald-500" />
              Jemputan Pukal Ibu Bapa
            </DialogTitle>
          </DialogHeader>
          {loadingBulkEligible ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              <span className="ml-3 text-gray-600 font-medium">Mencari ibu bapa tanpa akaun...</span>
            </div>
          ) : bulkEligible.length === 0 ? (
            <div data-testid="bulk-invite-empty" className="text-center py-10">
              <span className="text-5xl block mb-3">🎉</span>
              <p className="text-gray-600 font-bold">Semua ibu bapa (dengan email & telefon) sudah ada akaun!</p>
              <p className="text-xs text-gray-500 mt-2">Nota: Kanak-kanak tanpa email/telefon ibu bapa tidak disenaraikan.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                <p data-testid="bulk-invite-count" className="text-sm text-emerald-700 font-bold">
                  📧 {bulkEligible.length} ibu bapa akan menerima emel jemputan (akaun akan dicipta secara automatik)
                </p>
                <p className="text-xs text-emerald-600 mt-1">Setiap penghantaran akan direkodkan dalam Log Jemputan Email.</p>
              </div>
              <div className="max-h-72 overflow-y-auto space-y-1.5">
                {bulkEligible.map((c) => (
                  <div key={c.id} className="border border-gray-200 rounded-xl p-2.5 text-sm flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-bold text-gray-800 truncate">👶 {c.name}</p>
                      <p className="text-xs text-gray-600 truncate">{c.parent_name || "Ibu/Bapa"} • {c.parent_email} • {c.parent_phone}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  disabled={sendingBulkInvite}
                  onClick={() => setShowBulkInviteDialog(false)}
                >
                  Batal
                </Button>
                <Button
                  data-testid="confirm-bulk-invite-btn"
                  type="button"
                  className="flex-1 bg-gradient-to-r from-emerald-400 to-green-500 hover:from-emerald-500 hover:to-green-600 text-white"
                  disabled={sendingBulkInvite}
                  onClick={handleBulkInvite}
                >
                  {sendingBulkInvite ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Menghantar...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Hantar Semua ({bulkEligible.length})
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Parents Not Logged In Dialog */}
      <Dialog open={showNotLoggedInDialog} onOpenChange={setShowNotLoggedInDialog}>
        <DialogContent data-testid="not-logged-in-dialog" className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Ibu Bapa Belum Log Masuk
            </DialogTitle>
          </DialogHeader>
          {loadingNotLoggedIn ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
              <span className="ml-3 text-gray-600 font-medium">Memuatkan senarai...</span>
            </div>
          ) : notLoggedInParents.length === 0 ? (
            <div data-testid="not-logged-in-empty" className="text-center py-10">
              <span className="text-5xl block mb-3">🎉</span>
              <p className="text-gray-600 font-bold">Semua ibu bapa yang dijemput sudah log masuk!</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <p data-testid="not-logged-in-count" className="text-sm text-amber-700 font-bold">
                  ⚠️ {notLoggedInParents.length} ibu bapa telah dijemput tetapi masih belum log masuk
                </p>
                <p className="text-xs text-amber-600 mt-1">Gunakan "Hantar Semula" dalam Log Jemputan Email untuk hantar jemputan sekali lagi.</p>
              </div>
              <div className="space-y-2">
                {notLoggedInParents.map((p) => (
                  <div key={p.id} data-testid={`not-logged-in-item-${p.id}`} className="border border-gray-200 rounded-xl p-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-bold text-sm text-gray-800 truncate">👤 {p.name}</p>
                      <p className="text-xs text-gray-600 truncate">📱 {p.phone}{p.email ? ` • 📧 ${p.email}` : ""}</p>
                      {p.children?.length > 0 && (
                        <p className="text-xs text-gray-600 truncate">👶 {p.children.join(", ")}</p>
                      )}
                      <p className="text-xs text-gray-500">
                        {p.branch ? `${p.branch} • ` : ""}Dijemput: {p.invited_at ? new Date(p.invited_at).toLocaleString("ms-MY") : "-"}
                      </p>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold shrink-0 ${p.last_invite_status === "sent" ? "bg-green-100 text-green-700" : p.last_invite_status === "failed" ? "bg-red-100 text-red-600" : "bg-gray-100 text-gray-600"}`}>
                      {p.last_invite_status === "sent" ? "📧 Emel Dihantar" : p.last_invite_status === "failed" ? "❌ Emel Gagal" : "🕓 Dijemput"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Duplicate Management Dialog */}
      <ShareFrameDialog open={!!shareChild} onOpenChange={(o) => !o && setShareChild(null)} child={shareChild} frames={shareChild ? framesForChild(shareChild, activeFrames) : []} />
      {user.role === "superadmin" && <FrameSettingsDialog open={showFrameSettings} onOpenChange={setShowFrameSettings} />}

      <AlertDialog open={confirmMergeAll} onOpenChange={(o) => !mergingAll && setConfirmMergeAll(o)}>
        <AlertDialogContent className="z-[10001]" data-testid="merge-all-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Gabung semua rekod duplicate?</AlertDialogTitle>
            <AlertDialogDescription>
              {duplicates.groups?.length || 0} kumpulan ({duplicates.total_duplicates || 0} rekod duplicate) akan digabungkan ke rekod ASAL (rekod paling lama).
              Aktiviti, menu, log harian, perkembangan, pendaftaran dan ibu bapa dari rekod duplicate akan dipindahkan ke rekod ASAL, kemudian rekod duplicate dipadam.
              Tindakan ini tidak boleh dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={mergingAll} data-testid="merge-all-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleMergeAllDuplicates(); }}
              disabled={mergingAll}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="merge-all-confirm"
            >
              {mergingAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Merge className="w-4 h-4 mr-2" />}
              Ya, Gabung Semua
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Pengesanan Kanak-kanak Duplicate
            </DialogTitle>
          </DialogHeader>
          
          {loadingDuplicates ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
              <span className="ml-3 text-gray-600 font-medium">Mencari duplicates...</span>
            </div>
          ) : duplicates.total_duplicates === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-3xl">✅</span>
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">Tiada Duplicate Dijumpai!</h3>
              <p className="text-gray-600 text-sm">Semua rekod kanak-kanak adalah unik.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Summary */}
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                      <Copy className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-orange-800">
                        {duplicates.total_duplicates} Rekod Duplicate Dijumpai
                      </h3>
                      <p className="text-sm text-orange-600">
                        Dalam {duplicates.groups.length} kumpulan nama yang sama
                      </p>
                    </div>
                  </div>
                  {/* Bulk Gabung Semua kumpulan - Penyelia & Superadmin */}
                  {(user.role === 'superadmin' || user.role === 'admin') && (
                    <Button
                      onClick={() => setConfirmMergeAll(true)}
                      disabled={loadingDuplicates || mergingAll}
                      className="shrink-0 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white"
                      data-testid="merge-all-duplicates-btn"
                    >
                      {mergingAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Merge className="w-4 h-4 mr-2" />}
                      Gabung Semua ({duplicates.groups.length})
                    </Button>
                  )}
                </div>
              </div>

              {/* Duplicate Groups */}
              <div className="space-y-4">
                {duplicates.groups.map((group, groupIdx) => (
                  <div 
                    key={groupIdx}
                    className="border-2 border-orange-200 rounded-xl overflow-hidden"
                  >
                    {/* Group Header */}
                    <div className="bg-orange-100 px-4 py-3 flex items-center justify-between">
                      <div>
                        <h4 className="font-bold text-gray-800">
                          👶 {group.name}
                        </h4>
                        <p className="text-xs text-gray-600">
                          Tarikh Lahir: {group.dob} • {group.count} rekod sama
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleMergeDuplicates(group.original_id, group.duplicate_ids, group.name)}
                        className="bg-gradient-to-r from-purple-400 to-purple-500 hover:from-purple-500 hover:to-purple-600 text-white text-xs"
                      >
                        <Merge className="w-3 h-3 mr-1" />
                        Gabung Semua
                      </Button>
                    </div>

                    {/* Children List */}
                    <div className="divide-y divide-gray-100">
                      {group.children.map((child, childIdx) => (
                        <div 
                          key={child.id}
                          className={`px-4 py-3 flex items-center justify-between ${
                            childIdx === 0 ? 'bg-green-50' : 'bg-white hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                              childIdx === 0 
                                ? 'bg-green-200 text-green-700' 
                                : 'bg-orange-200 text-orange-700'
                            }`}>
                              {childIdx === 0 ? '✓' : childIdx}
                            </div>
                            <div>
                              <p className="font-semibold text-gray-800 text-sm">
                                {child.name}
                                {childIdx === 0 && (
                                  <span className="ml-2 px-2 py-0.5 bg-green-500 text-white text-xs rounded-full">
                                    ASAL
                                  </span>
                                )}
                              </p>
                              <p className="text-xs text-gray-500">
                                {child.class_name || 'Tiada kelas'} • {child.branch || 'Tiada cawangan'}
                              </p>
                            </div>
                          </div>
                          
                          {childIdx !== 0 && (
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteDuplicate(child.id, child.name)}
                              disabled={deletingDuplicate === child.id}
                              className="text-xs"
                            >
                              {deletingDuplicate === child.id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <>
                                  <Trash2 className="w-3 h-3 mr-1" />
                                  Padam
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Help Text */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                <p className="text-xs text-blue-700">
                  <span className="font-semibold">💡 Petunjuk:</span>
                  <br />• Rekod dengan label <span className="px-1 py-0.5 bg-green-500 text-white text-xs rounded">ASAL</span> adalah rekod paling lama dan akan dikekalkan.
                  <br />• &quot;Gabung Semua&quot; akan memindahkan semua aktiviti dari rekod duplicate ke rekod asal sebelum memadam. Butang &quot;Gabung Semua (N)&quot; di atas menggabungkan semua kumpulan sekali gus.
                  <br />• &quot;Padam&quot; akan memadam rekod individu tanpa memindahkan data.
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-end pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setShowDuplicateDialog(false)}
            >
              Tutup
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}